import { DayOfWeekEnum, Employment, WorkSchedule } from "@oysterhr/schema"
import { WorkScheduleFormValues } from "./WorkSchedule"
import { DateTime } from "luxon"
import * as Yup from "yup"
import { SharedPositionDetailsValues } from "../PositionDetails/types"
import {
  getHoursPerDaySchedule,
  getHoursPerWeekFromWorkSchedule,
} from "@oysterhr/hiring/full-time-employee"
import type { TFunction } from "react-i18next"

const SORTED_DAYS_OF_WEEK = [
  DayOfWeekEnum.Monday,
  DayOfWeekEnum.Tuesday,
  DayOfWeekEnum.Wednesday,
  DayOfWeekEnum.Thursday,
  DayOfWeekEnum.Friday,
  DayOfWeekEnum.Saturday,
  DayOfWeekEnum.Sunday,
]

export const sortWorkSchedule = (a: WorkSchedule, b: WorkSchedule) => {
  return SORTED_DAYS_OF_WEEK.indexOf(a.day) - SORTED_DAYS_OF_WEEK.indexOf(b.day)
}
export const sortDayOfWeek = (a: string, b: string) => {
  return (
    SORTED_DAYS_OF_WEEK.indexOf(a as DayOfWeekEnum) -
    SORTED_DAYS_OF_WEEK.indexOf(b as DayOfWeekEnum)
  )
}

export const getWorkScheduleInitialValues: (
  employmentSchedules: Employment["workSchedules"],
) => WorkScheduleFormValues["workDays"] = employmentSchedules => {
  if (!employmentSchedules?.length) return {}

  return (
    employmentSchedules
      // Can't sort array in place, we need to duplicate it
      .slice()
      .sort(sortWorkSchedule)
      .reduce(
        (formScheduleAcc, currentEngagementSchedule) => {
          return {
            ...formScheduleAcc,
            [currentEngagementSchedule.day]: {
              from: currentEngagementSchedule.startTime,
              to: currentEngagementSchedule.endTime,
            },
          }
        },
        {} as WorkScheduleFormValues["workDays"],
      )
  )
}

export const getWorkScheduleSubmitValues: (
  formSchedules: WorkScheduleFormValues["workDays"],
) => Employment["workSchedules"] = formSchedules => {
  if (!formSchedules) return []

  return Object.entries(formSchedules)
    .map<WorkSchedule | null>(([day, schedule]) => {
      // When the user selects and deselects a day, the schedule is undefined
      if (!schedule) return null

      const { from, to } = schedule
      return {
        day: day as DayOfWeekEnum,
        startTime: DateTime.fromISO(from).toISOTime({ suppressSeconds: true }),
        endTime: DateTime.fromISO(to).toISOTime({ suppressSeconds: true }),
      }
    })
    .filter(value => value !== null) as Array<WorkSchedule>
}

export const getWorkScheduleValidationSchema = (t: TFunction) => {
  return Yup.object().shape({
    workDays: Yup.object().when(
      "fullTimeHoursPerWeek",
      (hoursPerWeek: SharedPositionDetailsValues["fullTimeHoursPerWeek"]) => {
        return Yup.object<WorkScheduleFormValues["workDays"]>()
          .test(
            "match-hours-per-week",
            t("validation.workScheduleMatchHoursPerWeek", {
              ns: "company_interface/CompanyNewHireFTEPage",
            }),
            workDays => {
              const scheduledWeekHours = getHoursPerWeekFromWorkSchedule(workDays)
              if (scheduledWeekHours === 0) {
                // Skip validation if work schedule is not set
                return true
              }

              return parseFloat(hoursPerWeek) === scheduledWeekHours
            },
          )
          .test(
            "max-hours-per-day",
            t("validation.workScheduleMaxHoursPerDay", {
              ns: "company_interface/CompanyNewHireFTEPage",
            }),
            workDays => {
              return Object.values(workDays || {})
                .filter(Boolean)
                .every(daySchedule => {
                  if (!daySchedule.from || !daySchedule.to) {
                    return true
                  }
                  return getHoursPerDaySchedule(daySchedule) <= 8
                })
            },
          )
      },
    ),
  })
}
